import * as React from "react";
import { Users, MessageSquare, Target, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function SeamlessClientCoordination() {
  const benefits = [{
    icon: MessageSquare,
    title: "Clear Communication",
    description: "Maintain consistent dialogue with clients and vendors to prevent misunderstandings"
  }, {
    icon: Target,
    title: "Scope Management",
    description: "Keep projects on track by managing expectations and preventing scope creep"
  }, {
    icon: CheckCircle,
    title: "Stakeholder Coordination",
    description: "Seamlessly coordinate between all project stakeholders for efficient execution"
  }];
  return <section className="relative py-20 bg-background-dark">
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1399276/img/coordination_background_pattern.png)] bg-cover bg-center opacity-5" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Users className="size-5" />
            <span className="text-sm font-semibold">Relationship Management</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Seamless Client Coordination
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Our Account & Relationship Management Support ensures smooth interactions with clients and stakeholders, 
            eliminating communication gaps and keeping your projects aligned with business goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <Card key={i} data-index={i} className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 hover:shadow-strong transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]">
                <CardContent className="pt-8">
                  <div className="size-14 rounded-lg bg-accent/10 flex items-center justify-center mb-6">
                    <Icon className="size-7 text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        <Card className="bg-gradient-to-br from-primary/20 to-secondary/20 border-primary/30 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-white">
              What We Handle For You
            </CardTitle>
            <CardDescription className="text-gray-300">
              Comprehensive relationship management to keep your business running smoothly
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div data-index={0} className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="size-5 text-accent mt-1 shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Client Communication</h4>
                    <p className="text-sm text-gray-300">Regular updates and proactive outreach to maintain strong relationships</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="size-5 text-accent mt-1 shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Vendor Coordination</h4>
                    <p className="text-sm text-gray-300">Manage vendor relationships and ensure deliverables meet expectations</p>
                  </div>
                </div>
              </div>
              <div data-index={1} className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="size-5 text-accent mt-1 shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Issue Resolution</h4>
                    <p className="text-sm text-gray-300">Quickly address concerns before they escalate into major problems</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="size-5 text-accent mt-1 shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Documentation</h4>
                    <p className="text-sm text-gray-300">Maintain clear records of all interactions and agreements</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-12">
          <Link to="/ContactPage">
            <Button size="lg" className="bg-accent text-white hover:bg-accent-hover shadow-medium hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
              Start Improving Your Client Relationships
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}